"use client";

import { Apple, ChevronDown, Play, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function FutureProofYourBusiness() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = [{
    label: "EST. 2024"
  }, {
    label: "AI ARCHITECTS"
  }, {
    label: "OPERATIONAL EXCELLENCE"
  }];
  return <section data-section-id="870" id="future-proof-your-business" className="relative bg-background text-foreground overflow-hidden min-h-[92vh] flex items-center">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse 60% 50% at 10% 90%, hsl(215 15% 55% / 0.18), transparent 60%)"
    }} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 pb-24 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-7 order-2 lg:order-1 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-8">
              <span className="relative flex items-center">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg opacity-75" style={{
                  backgroundColor: "hsl(215 15% 55%)"
                }} />
                  <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
                  backgroundColor: "hsl(215 15% 55%)"
                }} />
                </span>
              </span>
              {stats.map((s, i) => <span key={i} data-index={i} className="flex items-center gap-3">
                  {i > 0 && <span className="text-muted-foreground/40">•</span>}
                  <span>{s.label}</span>
                </span>)}
            </div>

            {}
            <h1 className="relative font-default font-bold tracking-tighter text-foreground leading-[1.02] mb-8" style={{
            fontSize: "clamp(48px, 9vw, 140px)",
            letterSpacing: "-0.02em"
          }}>
              AutolexaAi
              <span className="absolute bottom-2 left-0 h-[0.05em] w-1/3 min-w-[120px]" style={{
              backgroundColor: "var(--chart-2)"
            }} aria-hidden="true" />
            </h1>

            {}
            <p className="font-default text-[18px] md:text-[20px] leading-relaxed text-muted-foreground max-w-[520px] mb-10">
              AutolexaAi streamlines your operations with intelligent automation, from customer support to workflow integration, saving you time and scaling efficiency.
            </p>

            {}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <Link to="/ConnectPage">
                <Button size="lg" className="rounded-lg h-14 px-7 bg-foreground/70 text-background hover:bg-foreground/60 font-medium text-base shadow-lg">
                  <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.02.6-1.56.3z" />
                  </svg>
                  Book Consultation
                  <Play className="size-4 fill-current" />
                </Button>
              </Link>

              <Button size="lg" variant="outline" className="rounded-lg h-14 px-7 bg-transparent border-border text-foreground hover:bg-foreground/5 hover:text-foreground font-medium text-base">
                <Apple className="size-5" />
                View Services
              </Button>
            </div>

            {}
            <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map(i => <Star key={i} data-index={i} className="size-3.5 fill-current" style={{
                color: "hsl(215 15% 55%)"
              }} />)}
              </div>
              <span>500+ workflows automated</span>
              <span className="text-muted-foreground/40">•</span>
              <span>AI Agency Leaders</span>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 order-1 lg:order-2 relative transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative lg:-mr-12 xl:-mr-24">
              {}
              <div className="absolute -inset-4 rounded-2xl blur-2xl opacity-40" style={{
              background: "radial-gradient(circle, hsl(215 15% 55% / 0.4), transparent 70%)"
            }} aria-hidden="true" />

              <div className="relative rounded-2xl overflow-hidden border border-border/60" style={{
              boxShadow: "0 0 0 1px hsl(215 15% 55% / 0.15), 0 30px 60px -15px hsl(0 0% 0% / 0.6)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_automation_dashboard.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_automation_dashboard.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_automation_dashboard.png-wvc-sizes" alt="AutolexaAi dashboard interface showing automated workflows" loading="lazy" decoding="async" className="w-full h-[560px] md:h-[640px] object-cover" />
                {}
                <div className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, hsl(220 12% 7% / 0.3) 0%, transparent 30%, transparent 60%, hsl(220 12% 7% / 0.7) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute bottom-6 left-6 right-6 sm:right-auto sm:max-w-sm">
                <div className="bg-background/60 backdrop-blur-lg border border-border/60 rounded-xl p-4 shadow-2xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_process_thumb.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_process_thumb.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_process_thumb.png-wvc-sizes" alt="AI automation process thumbnail" loading="lazy" decoding="async" className="w-14 h-14 rounded-md object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-1.5">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-lg opacity-75" style={{
                          backgroundColor: "hsl(215 15% 55%)"
                        }} />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-lg" style={{
                          backgroundColor: "hsl(215 15% 55%)"
                        }} />
                        </span>
                        Live Automation
                      </div>
                      <div className="font-default font-semibold text-sm text-foreground truncate">
                        Customer Support AI
                      </div>
                      <div className="font-default text-xs text-muted-foreground truncate">
                        Active Integration
                      </div>
                    </div>
                    <Button size="icon" className="rounded-lg h-12 w-12 bg-foreground/70 text-background hover:bg-foreground/60 shadow-lg flex-shrink-0" aria-label="View process">
                      <Play className="size-5 fill-current ml-0.5" />
                    </Button>
                  </div>

                  {}
                  <div className="space-y-1.5">
                    <div className="h-1 w-full bg-foreground/10 rounded-lg overflow-hidden">
                      <div className="h-full rounded-lg" style={{
                      width: "85%",
                      backgroundColor: "hsl(215 15% 55%)"
                    }} />
                    </div>
                    <div className="flex justify-between font-mono text-[10px] text-muted-foreground">
                      <span>Efficiency</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-10 left-0 right-0 pointer-events-none px-6 lg:px-12">
        <svg viewBox="0 0 1400 60" className="w-full h-10 opacity-30" preserveAspectRatio="none" aria-hidden="true">
          {Array.from({
          length: 120
        }).map((_, i) => {
          const h = 8 + Math.abs(Math.sin(i * 0.4)) * 40 + Math.abs(Math.cos(i * 0.27)) * 12;
          return <rect key={i} data-index={i} x={i * 12} y={30 - h / 2} width="3" height={h} rx="1.5" fill="currentColor" className="text-muted-foreground" style={{
            animation: `wave-pulse 2.4s ease-in-out ${i % 12 * 0.1}s infinite`
          }} />;
        })}
        </svg>
      </div>

      {}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        <span>Scroll</span>
        <ChevronDown className="size-3 animate-bounce" />
      </div>

      <style>{`
        @keyframes wave-pulse {
          0%, 100% { transform: scaleY(0.6); transform-origin: center; }
          50% { transform: scaleY(1.1); transform-origin: center; }
        }
      `}</style>
    </section>;
}